import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Grid3X3, X, Search, Pencil, Trash2, AlertTriangle, Package } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import toast from 'react-hot-toast';
import { zonesApi, camerasApi, organizationsApi } from '../services/api';
import { useAuthStore } from '../services/auth';
import type { ZoneWithCount } from '../types';

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function Zones() {
  const { organizationId } = useAuthStore();
  const [selectedZone, setSelectedZone] = useState<ZoneWithCount | null>(null);
  const [editName, setEditName] = useState('');
  const [editThreshold, setEditThreshold] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [cameraFilter, setCameraFilter] = useState<string>('all');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const queryClient = useQueryClient();

  // Check if zone monitoring is enabled
  const { data: organization, isLoading: orgLoading } = useQuery({
    queryKey: ['organization', organizationId],
    queryFn: () => organizationsApi.get(organizationId!),
    enabled: !!organizationId,
  });

  // Fetch cameras for filter
  const { data: cameras } = useQuery({
    queryKey: ['cameras'],
    queryFn: () => camerasApi.list(),
  });

  // Fetch zone analytics summary (includes zones with counts)
  const { data: zoneSummary, isLoading: zonesLoading } = useQuery({
    queryKey: ['zones-summary'],
    queryFn: () => zonesApi.getAnalyticsSummary(),
    enabled: !!organization?.zone_monitoring_enabled,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const updateZoneMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name?: string; low_stock_threshold?: number | null } }) =>
      zonesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['zones-summary'] });
      setSelectedZone(null);
      toast.success('Zone updated');
    },
    onError: () => {
      toast.error('Failed to update zone');
    },
  });

  const deleteZoneMutation = useMutation({
    mutationFn: (id: string) => zonesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['zones-summary'] });
      setSelectedZone(null);
      setShowDeleteConfirm(false);
      toast.success('Zone deleted');
    },
    onError: () => {
      toast.error('Failed to delete zone');
    },
  });

  const handleSave = () => {
    if (!selectedZone) return;
    const threshold = editThreshold.trim() ? parseInt(editThreshold, 10) : null;
    updateZoneMutation.mutate({
      id: selectedZone.zone.id,
      data: {
        name: editName.trim() || undefined,
        low_stock_threshold: threshold,
      },
    });
  };

  const handleOpenZone = (zoneWithCount: ZoneWithCount) => {
    setSelectedZone(zoneWithCount);
    setEditName(zoneWithCount.zone.name);
    setEditThreshold(zoneWithCount.zone.low_stock_threshold?.toString() || '');
    setShowDeleteConfirm(false);
  };

  const handleCloseZone = () => {
    setSelectedZone(null);
    setEditName('');
    setEditThreshold('');
    setShowDeleteConfirm(false);
  };

  // Filter zones by search and camera
  const filteredZones = zoneSummary?.zones_with_counts?.filter((zc) => {
    if (cameraFilter !== 'all' && zc.zone.camera_id !== cameraFilter) return false;
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      zc.zone.name.toLowerCase().includes(query) ||
      zc.camera_name?.toLowerCase().includes(query)
    );
  });

  // Get cameras that have zones or support zone mode
  const zoneCameras = cameras?.filter((c) =>
    c.detection_mode === 'zone' || c.detection_mode === 'both'
  );

  // Show message if zone monitoring is not enabled
  if (!orgLoading && organization && !organization.zone_monitoring_enabled) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Zones</h1>
          <p className="text-gray-500 dark:text-slate-400">Monitor item counts in defined zones</p>
        </div>
        <Card className="text-center py-12">
          <Grid3X3 className="w-12 h-12 text-gray-400 dark:text-slate-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Zone Monitoring is Disabled
          </h3>
          <p className="text-gray-500 dark:text-slate-400 mb-4 max-w-md mx-auto">
            Enable zone monitoring in Settings to start tracking item counts in defined areas.
          </p>
          <Button variant="outline" onClick={() => window.location.href = '/settings'}>
            Go to Settings
          </Button>
        </Card>
      </div>
    );
  }

  const isLoading = orgLoading || zonesLoading;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Zones</h1>
          <p className="text-gray-500 dark:text-slate-400">
            {zoneSummary?.total_zones || 0} zone{zoneSummary?.total_zones !== 1 ? 's' : ''} configured
            {zoneSummary?.low_stock_alerts ? (
              <span className="text-amber-600 ml-2">
                ({zoneSummary.low_stock_alerts} low stock alert{zoneSummary.low_stock_alerts !== 1 ? 's' : ''})
              </span>
            ) : null}
          </p>
        </div>
      </div>

      {/* Summary Stats */}
      {zoneSummary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Grid3X3 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-slate-400">Total Zones</p>
                <p className="text-xl font-semibold">{zoneSummary.total_zones}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <Package className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-slate-400">Total Items</p>
                <p className="text-xl font-semibold">{zoneSummary.total_items}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${zoneSummary.low_stock_alerts > 0 ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-gray-100 dark:bg-slate-700'}`}>
                <AlertTriangle className={`w-5 h-5 ${zoneSummary.low_stock_alerts > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400 dark:text-slate-500'}`} />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-slate-400">Low Stock Alerts</p>
                <p className="text-xl font-semibold">{zoneSummary.low_stock_alerts}</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-slate-500" />
          <input
            type="text"
            placeholder="Search zones..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg dark:bg-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <select
          value={cameraFilter}
          onChange={(e) => setCameraFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg dark:bg-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="all">All Cameras</option>
          {zoneCameras?.map((camera) => (
            <option key={camera.id} value={camera.id}>
              {camera.name}
            </option>
          ))}
        </select>
      </div>

      {/* Zones Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="h-40 bg-gray-200 dark:bg-slate-700 rounded-lg mb-2" />
              <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-3/4" />
            </div>
          ))}
        </div>
      ) : filteredZones?.length === 0 ? (
        <Card className="text-center py-12">
          <Grid3X3 className="w-12 h-12 text-gray-400 dark:text-slate-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {searchQuery || cameraFilter !== 'all' ? 'No zones found' : 'No zones configured yet'}
          </h3>
          <p className="text-gray-500 dark:text-slate-400">
            {searchQuery || cameraFilter !== 'all'
              ? 'Try a different search or filter'
              : 'Configure zones in camera settings to start monitoring'}
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredZones?.map((zc) => {
            const isLowStock = zc.zone.low_stock_threshold !== null &&
              zc.current_count !== null &&
              zc.current_count < zc.zone.low_stock_threshold;

            return (
              <button
                key={zc.zone.id}
                onClick={() => handleOpenZone(zc)}
                className="text-left group"
              >
                <Card className={`p-4 transition-colors ${isLowStock ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-500' : 'hover:bg-gray-50 dark:hover:bg-slate-700'}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 dark:text-white truncate">{zc.zone.name}</h3>
                      <p className="text-sm text-gray-500 dark:text-slate-400 truncate">{zc.camera_name || 'Unknown camera'}</p>
                    </div>
                    {isLowStock && (
                      <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 ml-2" />
                    )}
                  </div>
                  <div className="flex items-end justify-between">
                    <div>
                      <p className="text-3xl font-bold text-gray-900 dark:text-white">
                        {zc.current_count ?? '—'}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-slate-400">items</p>
                    </div>
                    {zc.zone.low_stock_threshold !== null && (
                      <p className={`text-sm ${isLowStock ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400 dark:text-slate-500'}`}>
                        Threshold: {zc.zone.low_stock_threshold}
                      </p>
                    )}
                  </div>
                </Card>
              </button>
            );
          })}
        </div>
      )}

      {/* Zone Detail Modal */}
      {selectedZone && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md relative">
            <button
              onClick={handleCloseZone}
              className="absolute top-4 right-4 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white pr-8">
                Zone Details
              </h2>

              {/* Current Count */}
              <div className="text-center py-4 bg-gray-50 dark:bg-slate-900 rounded-lg">
                <p className="text-4xl font-bold text-gray-900 dark:text-white">
                  {selectedZone.current_count ?? '—'}
                </p>
                <p className="text-sm text-gray-500 dark:text-slate-400">Current items</p>
              </div>

              {/* Name Input */}
              <Input
                label="Zone Name"
                placeholder="Enter zone name..."
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
              />

              {/* Threshold Input */}
              <Input
                label="Low Stock Threshold (optional)"
                type="number"
                placeholder="e.g., 5"
                value={editThreshold}
                onChange={(e) => setEditThreshold(e.target.value)}
              />
              <p className="text-xs text-gray-500 dark:text-slate-400 -mt-2">
                Alert when item count falls below this number
              </p>

              {/* Metadata */}
              <div className="text-sm text-gray-500 dark:text-slate-400 space-y-1">
                <p>Camera: {selectedZone.camera_name || 'Unknown'}</p>
                <p>Created: {formatDate(selectedZone.zone.created_at)}</p>
              </div>

              {/* Actions */}
              {!showDeleteConfirm ? (
                <div className="flex gap-3">
                  <Button
                    onClick={handleSave}
                    isLoading={updateZoneMutation.isPending}
                    className="flex-1"
                  >
                    <Pencil className="w-4 h-4 mr-2" />
                    Save Changes
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg space-y-3">
                  <p className="text-sm text-red-800 dark:text-red-300">
                    Delete this zone? This will also delete all associated count history.
                  </p>
                  <div className="flex gap-3">
                    <Button
                      variant="danger"
                      onClick={() => deleteZoneMutation.mutate(selectedZone.zone.id)}
                      isLoading={deleteZoneMutation.isPending}
                    >
                      Delete
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setShowDeleteConfirm(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
